import { useSearchParams } from "react-router";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { logger } from "@/lib/logger";

// Constant-length JSX expression keeps prettier output stable for any rendered name.
const PROJECT_NAME = "Customer Collaboration Agent Platform";

// Human-readable copy for each failure code the SSO callback can redirect with.
const ERROR_MESSAGES: Record<string, string> = {
  "sso-failed": "Sign-in failed. Please try again.",
  "missing-email": "Your Microsoft account has no email address we can use.",
  "domain-not-allowed": "This email domain isn't allowed on this platform.",
  "provisioning-failed": "We couldn't set up your account. Contact an administrator.",
  "no-customer-account":
    "We couldn't find a customer account for you. Ask your J&J contact to add you.",
  "sso-unavailable": "Customer sign-in is temporarily unavailable. Please try again shortly.",
};

// Four-square Microsoft logo, inline so the page pulls in no external asset.
function MicrosoftLogo() {
  return (
    <svg
      viewBox="0 0 21 21"
      className="h-4 w-4"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

export function LoginPage() {
  const [searchParams] = useSearchParams();
  const errorCode = searchParams.get("error");
  const errorMessage = errorCode ? (ERROR_MESSAGES[errorCode] ?? ERROR_MESSAGES["sso-failed"]) : "";

  const handleStaffSignIn = () => {
    logger.debug("[login] starting J&J staff SSO sign-in → /api/auth/sso/login");
    // Full-page navigation: the backend issues a 302 to Microsoft and the
    // browser returns to the callback with the session cookie already set.
    window.location.href = "/api/auth/sso/login?next=/home";
  };

  const handleCustomerSignIn = () => {
    logger.debug("[login] starting customer sign-in → /api/auth/customer/login");
    // Same full-page federated flow, but routed to the customer's IdP; the
    // callback lands the customer in the self-service portal.
    window.location.href = "/api/auth/customer/login?next=/portal";
  };

  return (
    <div className="bg-background flex min-h-screen items-center justify-center px-4 py-10">
      <Card className="border-border shadow-primary/5 w-full max-w-md shadow-xl">
        <CardHeader className="space-y-4 text-center">
          <img src="/brand/logo.png" alt="Johnson & Johnson" className="mx-auto h-10 w-auto" />
          <div className="space-y-2">
            <p className="text-muted text-[10px] font-medium tracking-[0.18em] uppercase">
              Johnson &amp; Johnson MedTech
            </p>
            <CardTitle className="font-display text-foreground text-2xl font-normal tracking-tight">
              {PROJECT_NAME}
            </CardTitle>
            <p className="text-muted text-sm">
              Securely connect J&amp;J data with your organization&apos;s agents through MCP.
            </p>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {errorMessage && <p className="text-destructive text-center text-sm">{errorMessage}</p>}
          <Button type="button" onClick={handleStaffSignIn} className="w-full">
            <MicrosoftLogo />
            J&amp;J employee sign in
          </Button>

          <div className="flex items-center gap-3">
            <span className="border-border flex-1 border-t" aria-hidden />
            <span className="text-muted text-[10px] font-medium tracking-[0.18em] uppercase">
              or
            </span>
            <span className="border-border flex-1 border-t" aria-hidden />
          </div>

          <Button type="button" variant="outline" onClick={handleCustomerSignIn} className="w-full">
            Customer sign in
          </Button>
          <p className="text-muted text-center text-sm">
            J&amp;J staff sign in with their Microsoft work account. Customers sign in through their
            own organization&apos;s identity provider.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
